import { Assignment, Info, Person } from '@mui/icons-material';
import { Box, Chip, Grid, IconButton, Paper, Typography } from '@mui/material';
import { LightTooltip } from 'lib/designSystem/WhiteTooltip';
import { formatMoney } from 'lib/functions/utils';
import { Business } from 'lib/types/models';
import React from 'react';
import { useDispatch } from 'react-redux';
import { showBusinessNetwork } from 'services/redux/businessNetworkSlice';

export interface TenderItem {
  id: string;
  productName?: string | null;
  description?: string | null;
  quantity?: number | null;
  estimatedQuantity?: number | null;
  unitPrice?: number | null;
  supplier?: Business | null;
}

const ItemsSection: React.FC<{ items: TenderItem[] }> = ({ items }) => {
  const dispatch = useDispatch();

  const showSupplier = (id: string) => {
    const row = items.find((x: TenderItem) => x.id === id);
    if (!row?.supplier) return;
    dispatch(
      showBusinessNetwork({
        id: row.supplier?.identifier,
        name: row.supplier?.name,
        path: 'public-market',
      }),
    );
  };

  return (
    <>
      <Paper
        sx={{ p: 1.5, boxShadow: 'none', border: '1px solid #e0e0e0', mb: 2 }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 1,
            mb: 1.5,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              fontSize: '1rem',
              lineHeight: 1.1,
            }}
          >
            Items de la Licitación
          </Typography>
          <Chip
            label="Detalles"
            size="small"
            color="primary"
            variant="outlined"
            sx={{ height: '18px', fontSize: '0.65rem' }}
          />
        </Box>

        <Box
          sx={{
            maxHeight: '50vh',
            overflowY: 'auto',
            '&::-webkit-scrollbar': { width: '6px' },
            '&::-webkit-scrollbar-track': {
              backgroundColor: '#f1f1f1',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#c1c1c1',
              borderRadius: '3px',
              '&:hover': { backgroundColor: '#a8a8a8' },
            },
          }}
        >
          <Grid container spacing={1.5}>
            {items.map((item: TenderItem, index: number) => (
              <Grid item xs={12} key={item.id}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    border: '1px solid #f0f0f0',
                    backgroundColor: '#fafafa',
                  }}
                >
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box
                          sx={{
                            backgroundColor: 'rgba(25, 118, 210, 0.1)',
                            borderRadius: '50%',
                            p: 0.3,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Assignment
                            sx={{ fontSize: '0.7rem', color: 'primary.main' }}
                          />
                        </Box>
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: '0.6rem' }}
                          >
                            Item {index + 1}
                          </Typography>
                          <LightTooltip
                            title={
                              <Box
                                sx={{
                                  p: 1,
                                  maxWidth: 360,
                                  whiteSpace: 'normal',
                                  wordBreak: 'break-word',
                                }}
                              >
                                <Typography variant="body1">
                                  {item.productName ||
                                    item.description ||
                                    'Sin descripción'}
                                </Typography>
                              </Box>
                            }
                            placement="top"
                          >
                            <Typography
                              variant="body2"
                              fontWeight="medium"
                              sx={{
                                fontSize: '0.7rem',
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                              }}
                            >
                              {item.productName ||
                                item.description ||
                                'Sin descripción'}
                            </Typography>
                          </LightTooltip>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: '0.6rem' }}
                          >
                            Cantidad
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontSize: '0.7rem' }}
                          >
                            {item.quantity || item.estimatedQuantity || 'N/A'}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: '0.6rem' }}
                          >
                            Precio
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontSize: '0.7rem' }}
                          >
                            {item.unitPrice
                              ? formatMoney(item.unitPrice)
                              : 'N/A'}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                    {item.supplier && (
                      <Grid item xs={12}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Box
                            sx={{
                              backgroundColor: 'rgba(76, 175, 80, 0.1)',
                              borderRadius: '50%',
                              p: 0.3,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Person
                              sx={{ fontSize: '0.7rem', color: 'success.main' }}
                            />
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{ fontSize: '0.7rem' }}
                          >
                            {item.supplier.name} ({item.supplier.identifier})
                          </Typography>
                          <Box display="flex" alignItems="center" gap={1}>
                            <IconButton
                              size="small"
                              aria-label="View supplier details"
                              onClick={() => showSupplier(item.id)}
                            >
                              <Info fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Paper>
    </>
  );
};

export default ItemsSection;
